import { useEffect, useRef, useState } from "react";
import { Card, Button } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import Spinner1 from "../common/spinners";
import moment from "moment";
import { useVitals } from "../../context/vitalsContext";
import useVitalsStore from "../../store/patient/vitalsStore";

const VitalHistory = () => {
  const { handleFetchVitals, vitals: data = [], loading } = useVitals();
  const { saveVitals } = useVitalsStore();
  const [selectedVital, setSelectedVital] = useState(null);
  const [chartData, setChartData] = useState({ series: [], options: {} });
  const hasRequestedVitals = useRef(false);
  const vitalTypes = Array.from(new Set((Array.isArray(data) ? data : []).map((vital) => vital?.readingType).filter(Boolean)));

  useEffect(() => {
    if (!hasRequestedVitals.current) {
      hasRequestedVitals.current = true;
      handleFetchVitals();
    }
  }, [handleFetchVitals]);

  useEffect(() => {
    saveVitals(Array.isArray(data) ? data : []);
  }, [data, saveVitals]);

  useEffect(() => {
    if (!vitalTypes.length) {
      setSelectedVital(null);
      setChartData({ series: [], options: {} });
      return;
    }

    if (!selectedVital || !vitalTypes.includes(selectedVital)) {
      setSelectedVital(vitalTypes[0]);
      return;
    }

    updateChartData(selectedVital);
  }, [data, selectedVital]);

  const updateChartData = (vital) => {
    const vitalReadings = (Array.isArray(data) ? data : []).filter((v) => v?.readingType === vital);
    const categories = vitalReadings.map((v) =>
      new Date(v?.createdAt).toLocaleDateString()
    );
  
    let series = [];
  
    if (vital === "Blood Pressure") {
      series = [
        {
          name: "Systolic",
          data: vitalReadings.map((v) =>
            parseInt(v?.reading?.blood_pressure.split("/")[0], 10)
          ),
          color: "#0cb785",
        },
        {
          name: "Diastolic",
          data: vitalReadings.map((v) =>
            parseInt(v?.reading?.blood_pressure.split("/")[1], 10)
          ),
          color: "#008000",
        },
      ];
    } 
    else if (vital === "Fluid Chart") {
      // Handle fluid chart data - use volume which is numeric
      series = [
        {
          name: "Fluid Volume (ml)",
          data: vitalReadings.map((v) =>
            parseInt(v?.reading?.volume, 10) || 0
          ),
        },
      ];
    }
    else if (vital === "regimen") {
      // Handle regimen data - extract numeric part if exists
      series = [
        {
          name: "Regimen",
          data: vitalReadings.map((v) => {
            const value = v?.reading?.regimen;
            // Extract first numeric value found (e.g., "150MG" -> 150)
            const numericValue = value?.match(/\d+/)?.[0];
            return numericValue ? parseInt(numericValue, 10) : 0;
          }),
        },
      ];
    }
    else {
      // Default handling for other numeric vitals
      series = [
        {
          name: vital,
          data: vitalReadings.map((v) => {
            const value = Object.values(v?.reading)[0];
            return isNaN(value) ? 0 : parseFloat(value);
          }),
        },
      ];
    }
  
    setChartData({
      series,
      options: {
        chart: { type: "line", height: 350 },
        stroke: { curve: "smooth", width: 2 },
        colors: ["#0cb785", "#0cb785"],
        xaxis: { categories, title: { text: "Date" } },
        yaxis: { 
          title: { 
            text: vital === "Blood Pressure" ? "Blood Pressure (mmHg)" : 
                  vital === "Fluid Chart" ? "Volume (ml)" :
                  vital === "regimen" ? "Dosage" :
                  "Value" 
          } 
        },
        legend: { position: "top" },
      },
    });
  };

  // Function to get de last reading
  const getLastReading = () => {
    const vitalReadings = (Array.isArray(data) ? data : [])
      .filter((v) => v?.readingType === selectedVital)
      .sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt));
  
    if (!vitalReadings || vitalReadings.length === 0) {
      return "No reading yet";
    }
  
    const latestReading = vitalReadings[0];
    const readingDate = new Date(latestReading?.createdAt).toLocaleDateString();
  
    if (selectedVital === "Blood Pressure") {
      return `${latestReading?.reading?.blood_pressure} mmHg (Recorded on ${readingDate})`;
    } 
    else if (selectedVital === "Fluid Chart") {
      return `${latestReading?.reading?.volume}ml ${latestReading?.reading?.fluid_route} (Recorded on ${readingDate})`;
    }
    else if (selectedVital === "regimen") {
      return `${latestReading?.reading?.regimen} (Recorded on ${readingDate})`;
    }
    else {
      const value = Object.values(latestReading?.reading)[0];
      return `${value} (Recorded on ${moment(readingDate, "DD/MM/YYYY").format("ddd, Do MMMM YYYY")})`;
    }
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title as="h6">Vital History</Card.Title>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="w-100 d-flex justify-content-center align-items-center">
            <Spinner1 animation="border" />
          </div>
        ) : !vitalTypes.length ? (
          <div className="text-center py-4 text-secondary">
            No vital readings available yet.
          </div>
        ) : (
          <>
            <div className="d-flex gap-2 w-100 mb-3 flex-wrap">
              {vitalTypes.map((vitalType, index) => (
                <Button
                  key={index}
                  variant={selectedVital === vitalType ? "success" : "outline-success"}
                  onClick={() => setSelectedVital(vitalType)}
                  className={`${selectedVital !== vitalType ? "hover-text-white" : ""}`}
                >
                  {vitalType}
                </Button>
              ))}
            </div>

            <ReactApexChart
              series={chartData.series}
              options={chartData.options}
              type="area"
              height={350}
            />
            <p className="mt-3">
              <strong className=" text-success">Last Recorded {selectedVital}:</strong> <span className=" reading-text fw-bolder">{getLastReading()}</span>
            </p>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default VitalHistory;
